import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ArrowLeft, ArrowUp, ArrowDown, Trash2, Plus, Save, Eye, Download, Loader2, Heading1, Type, ListOrdered, Columns3, Table2, Sigma, MoveVertical, PanelBottom, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { BlokConfig } from "@/components/templates/BlokConfig";
import { BlokPreview } from "@/components/templates/BlokPreview";

export const BLOK_TYPES = [
  { type: "logo", label: "Bedrijfslogo", icon: ImageIcon },
  { type: "kop", label: "Kop / titel", icon: Heading1 },
  { type: "tekst", label: "Vrije tekst", icon: Type },
  { type: "velden", label: "Veldenlijst", icon: ListOrdered },
  { type: "kolommen", label: "Kolommen (2–3 naast elkaar)", icon: Columns3 },
  { type: "regeltabel", label: "Regeltabel (orderregels)", icon: Table2 },
  { type: "totalen", label: "Totalen", icon: Sigma },
  { type: "spacer", label: "Witruimte", icon: MoveVertical },
  { type: "footer", label: "Paginafooter", icon: PanelBottom },
];

const TYPE_LABELS = { pakbon: "Pakbon", verkoopfactuur: "Verkoopfactuur", samplebrief: "Samplebrief", productsheet: "Productsheet" };

export const LETTERTYPEN = [
  "Calibri", "Arial", "Helvetica", "Verdana", "Tahoma", "Georgia",
  "Times New Roman", "Garamond", "Trebuchet MS", "Courier New", "Avenir Next",
];
const STANDAARD_FONT = "_standaard";

let _keyTeller = 0;
const nieuweKey = () => `blk-${Date.now()}-${_keyTeller++}`;

function leegBlok(type) {
  const basis = { _key: nieuweKey(), type };
  if (type === "logo") return { ...basis, breedte_mm: 46, uitlijning: "links" };
  if (type === "kop") return { ...basis, tekst: "", grootte_px: 29, uitlijning: "links" };
  if (type === "tekst") return { ...basis, tekst: "", uitlijning: "links", vet: false };
  if (type === "velden" || type === "totalen") return { ...basis, items: [] };
  if (type === "kolommen") return { ...basis, kolommen: [{ titel: "", inhoud: "velden", velden: [] }] };
  if (type === "regeltabel") return { ...basis, kolommen: [] };
  if (type === "footer") return { ...basis, regels: ["{{ footer_bedrijf }}", "{{ footer_contact }}"] };
  return basis;
}

export default function TemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [design, setDesign] = useState(null);
  const [naam, setNaam] = useState("");
  const [lettertype, setLettertype] = useState(STANDAARD_FONT);
  const [blokken, setBlokken] = useState([]);
  const [velden, setVelden] = useState([]);
  const [tabelKolommen, setTabelKolommen] = useState([]);
  const [opslaan, setOpslaan] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  useEffect(() => {
    api.get(`/template-designs/${id}`).then(async ({ data }) => {
      setDesign(data);
      setNaam(data.naam);
      setLettertype(data.lettertype || STANDAARD_FONT);
      setBlokken((data.blokken || []).map((b) => ({ ...b, _key: nieuweKey() })));
      const { data: v } = await api.get(`/templates/velden/${data.type}`);
      setVelden(v.velden || []);
      setTabelKolommen(v.tabel_kolommen || []);
    }).catch(() => {
      toast.error("Template niet gevonden");
      navigate("/templates");
    });
  }, [id, navigate]);

  const schoneBlokken = () => blokken.map(({ _key, ...rest }) => rest);
  const huidigFont = lettertype === STANDAARD_FONT ? "" : lettertype;

  const bewaar = async () => {
    setOpslaan(true);
    try {
      await api.put(`/template-designs/${id}`, { naam, blokken: schoneBlokken(), lettertype: huidigFont });
      toast.success("Template opgeslagen");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Opslaan mislukt");
    } finally { setOpslaan(false); }
  };

  const voorbeeld = async (fmt) => {
    setPreviewBusy(true);
    try {
      const res = await api.post("/template-designs/voorbeeld",
        { type: design.type, blokken: schoneBlokken(), fmt, lettertype: huidigFont || null }, { responseType: "blob" });
      if (fmt === "pdf") {
        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        const url = URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = "voorbeeld.docx";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 2000);
      }
    } catch (err) {
      let msg = "Voorbeeld genereren mislukt";
      try {
        const txt = await err?.response?.data?.text?.();
        const j = JSON.parse(txt || "{}");
        if (j?.detail) msg = j.detail;
      } catch (parseErr) { /* laat standaardmelding staan; response was geen JSON */
        if (process.env.NODE_ENV === "development") console.debug("TemplateEditor preview error-body parse mislukt:", parseErr);
      }
      if (err?.response?.status === 502 && fmt === "pdf") {
        msg = "PDF-conversie is nu niet beschikbaar (CloudConvert). Probeer 'Voorbeeld (Word)'.";
      }
      toast.error(msg);
    } finally { setPreviewBusy(false); }
  };

  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= blokken.length) return;
    const arr = [...blokken];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setBlokken(arr);
  };

  if (!design) return <div className="text-sm text-muted-foreground p-6">Laden…</div>;

  return (
    <div className="space-y-5" data-testid="template-editor-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/templates" className="text-muted-foreground hover:text-foreground" data-testid="editor-terug">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <Input className="h-9 w-72 font-medium" value={naam} onChange={(e) => setNaam(e.target.value)}
            data-testid="editor-naam" />
          <Badge variant="outline">{TYPE_LABELS[design.type] || design.type}</Badge>
          <Select value={lettertype} onValueChange={setLettertype}>
            <SelectTrigger className="h-9 w-48" data-testid="editor-lettertype">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={STANDAARD_FONT}>Standaard (Calibri)</SelectItem>
              {LETTERTYPEN.filter((f) => f !== "Calibri").map((f) => (
                <SelectItem key={f} value={f}><span style={{ fontFamily: `'${f}', sans-serif` }}>{f}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => voorbeeld("docx")} disabled={previewBusy} data-testid="editor-voorbeeld-word">
            <Download className="h-4 w-4 mr-1" /> Voorbeeld (Word)
          </Button>
          <Button variant="outline" onClick={() => voorbeeld("pdf")} disabled={previewBusy} data-testid="editor-voorbeeld-pdf">
            {previewBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
            Voorbeeld (PDF)
          </Button>
          <Button onClick={bewaar} disabled={opslaan} data-testid="editor-opslaan">
            {opslaan ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Opslaan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* Linker kolom: blokken bewerken */}
        <div className="space-y-3">
          {blokken.map((blok, i) => {
            const meta = BLOK_TYPES.find((b) => b.type === blok.type) || {};
            const Icon = meta.icon || Type;
            return (
              <div key={blok._key} className="bg-card border border-border rounded-lg" data-testid={`editor-blok-${i}`}>
                <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <Icon className="h-3.5 w-3.5" /> {meta.label || blok.type}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, -1)} data-testid={`blok-omhoog-${i}`}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, 1)} data-testid={`blok-omlaag-${i}`}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                      onClick={() => setBlokken(blokken.filter((_, j) => j !== i))} data-testid={`blok-verwijderen-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="p-3">
                  <BlokConfig blok={blok} velden={velden} tabelKolommen={tabelKolommen}
                    onChange={(nieuw) => setBlokken(blokken.map((b, j) => (j === i ? nieuw : b)))} />
                </div>
              </div>
            );
          })}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full border-dashed" data-testid="blok-toevoegen">
                <Plus className="h-4 w-4 mr-1" /> Blok toevoegen
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64">
              {BLOK_TYPES.map((b) => {
                const Icon = b.icon;
                return (
                  <DropdownMenuItem key={b.type} onClick={() => setBlokken([...blokken, leegBlok(b.type)])}
                    data-testid={`blok-toevoegen-${b.type}`}>
                    <Icon className="h-4 w-4 mr-2" /> {b.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Rechter kolom: live-weergave */}
        <div className="lg:sticky lg:top-4">
          <div className="text-xs text-muted-foreground mb-2">Live-weergave (velden worden bij het genereren ingevuld met echte gegevens)</div>
          <BlokPreview blokken={blokken} lettertype={huidigFont} />
        </div>
      </div>
    </div>
  );
}
